import {
  doc,
  collection,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  onSnapshot,
  query,
  orderBy,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';

// ========== USER PROFILE ==========

export async function getUserProfile(userId) {
  const docRef = doc(db, 'users', userId, 'data', 'profile');
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : null;
}

export async function saveUserProfile(userId, profile) {
  const docRef = doc(db, 'users', userId, 'data', 'profile');
  await setDoc(docRef, {
    ...profile,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// Listener real-time per il profilo
export function subscribeToUserProfile(userId, callback) {
  const docRef = doc(db, 'users', userId, 'data', 'profile');
  return onSnapshot(docRef, (docSnap) => {
    callback(docSnap.exists() ? docSnap.data() : null);
  });
}

// ========== INVOICES ==========

export async function getInvoices(userId) {
  const collRef = collection(db, 'users', userId, 'invoices');
  const q = query(collRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

export async function saveInvoice(userId, invoice) {
  const { id, ...data } = invoice;
  const docRef = id
    ? doc(db, 'users', userId, 'invoices', id)
    : doc(collection(db, 'users', userId, 'invoices'));

  await setDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });

  return docRef.id;
}

export async function updateInvoice(userId, invoiceId, updates) {
  const docRef = doc(db, 'users', userId, 'invoices', invoiceId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteInvoice(userId, invoiceId) {
  const docRef = doc(db, 'users', userId, 'invoices', invoiceId);
  await deleteDoc(docRef);
}

// Listener real-time per le fatture
export function subscribeToInvoices(userId, callback) {
  const collRef = collection(db, 'users', userId, 'invoices');
  const q = query(collRef, orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const invoices = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(invoices);
  });
}

// ========== PENSION CONTRIBUTIONS ==========

export async function getPensionContributions(userId) {
  const collRef = collection(db, 'users', userId, 'pensionContributions');
  const q = query(collRef, orderBy('date', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

export async function savePensionContribution(userId, contribution) {
  const { id, ...data } = contribution;
  const docRef = id
    ? doc(db, 'users', userId, 'pensionContributions', id)
    : doc(collection(db, 'users', userId, 'pensionContributions'));

  await setDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });

  return docRef.id;
}

export async function deletePensionContribution(userId, contributionId) {
  const docRef = doc(db, 'users', userId, 'pensionContributions', contributionId);
  await deleteDoc(docRef);
}

// Listener real-time per i contributi pensione
export function subscribeToPensionContributions(userId, callback) {
  const collRef = collection(db, 'users', userId, 'pensionContributions');
  const q = query(collRef, orderBy('date', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const contributions = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(contributions);
  });
}

// ========== FISCAL SETTINGS ==========

export async function getFiscalSettings(userId) {
  const docRef = doc(db, 'users', userId, 'data', 'fiscalSettings');
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : null;
}

export async function saveFiscalSettings(userId, settings) {
  const docRef = doc(db, 'users', userId, 'data', 'fiscalSettings');
  await setDoc(docRef, {
    ...settings,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export function subscribeToFiscalSettings(userId, callback) {
  const docRef = doc(db, 'users', userId, 'data', 'fiscalSettings');
  return onSnapshot(docRef, (docSnap) => {
    callback(docSnap.exists() ? docSnap.data() : null);
  });
}

// ========== FISCAL BY YEAR (dati fiscali storicizzati per anno) ==========

export async function getFiscalByYear(userId) {
  const docRef = doc(db, 'users', userId, 'data', 'fiscalByYear');
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : null;
}

export async function saveFiscalByYear(userId, data) {
  const docRef = doc(db, 'users', userId, 'data', 'fiscalByYear');
  await setDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export function subscribeToFiscalByYear(userId, callback) {
  const docRef = doc(db, 'users', userId, 'data', 'fiscalByYear');
  return onSnapshot(docRef, (docSnap) => {
    callback(docSnap.exists() ? docSnap.data() : null);
  });
}

// ========== PENSION PLAN SETTINGS ==========

export async function getPensionPlan(userId) {
  const docRef = doc(db, 'users', userId, 'data', 'pensionPlan');
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : null;
}

export async function savePensionPlan(userId, plan) {
  const docRef = doc(db, 'users', userId, 'data', 'pensionPlan');
  await setDoc(docRef, {
    ...plan,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export function subscribeToPensionPlan(userId, callback) {
  const docRef = doc(db, 'users', userId, 'data', 'pensionPlan');
  return onSnapshot(docRef, (docSnap) => {
    callback(docSnap.exists() ? docSnap.data() : null);
  });
}

// ========== BULK OPERATIONS ==========

// Importa dati da localStorage a Firestore
export async function migrateLocalDataToFirestore(userId, localData) {
  const batch = writeBatch(db);

  // Profile
  if (localData.user) {
    const profileRef = doc(db, 'users', userId, 'data', 'profile');
    batch.set(profileRef, {
      ...localData.user,
      migratedAt: serverTimestamp(),
    });
  }

  // Fiscal settings
  if (localData.fiscalSettings) {
    const fiscalRef = doc(db, 'users', userId, 'data', 'fiscalSettings');
    batch.set(fiscalRef, {
      ...localData.fiscalSettings,
      migratedAt: serverTimestamp(),
    });
  }

  // Fiscal by year (dati fiscali storicizzati per anno)
  if (localData.fiscalByYear) {
    const fiscalByYearRef = doc(db, 'users', userId, 'data', 'fiscalByYear');
    batch.set(fiscalByYearRef, {
      ...localData.fiscalByYear,
      migratedAt: serverTimestamp(),
    });
  }

  // Pension plan (solo monthlyGoal)
  if (localData.pensionPlan) {
    const pensionPlanRef = doc(db, 'users', userId, 'data', 'pensionPlan');
    batch.set(pensionPlanRef, {
      monthlyGoal: localData.pensionPlan.monthlyGoal || 0,
      migratedAt: serverTimestamp(),
    });
  }

  // Commit batch per i documenti singoli
  await batch.commit();

  // Invoices (in batch separati per evitare limiti)
  if (localData.invoices && localData.invoices.length > 0) {
    // Filtra fatture corrotte (senza invoiceCode o senza id)
    const validInvoices = localData.invoices.filter((inv) => {
      if (!inv.id || !inv.invoiceCode) {
        console.warn('[Migration] Fattura scartata (manca id o invoiceCode):', inv);
        return false;
      }
      return true;
    });

    // Recupera fatture esistenti per evitare duplicati
    const existingSnapshot = await getDocs(collection(db, 'users', userId, 'invoices'));
    const existingIds = new Set(existingSnapshot.docs.map((d) => d.id));
    const existingCodes = new Set(existingSnapshot.docs.map((d) => d.data().invoiceCode).filter(Boolean));

    const newInvoices = validInvoices.filter((inv) => {
      if (existingIds.has(inv.id) || existingCodes.has(inv.invoiceCode)) {
        console.log('[Migration] Fattura già esistente, skip:', inv.invoiceCode);
        return false;
      }
      return true;
    });

    console.log(`[Migration] ${validInvoices.length} valide, ${newInvoices.length} nuove da importare`);

    const invoiceBatches = chunkArray(newInvoices, 400);
    for (const chunk of invoiceBatches) {
      const invBatch = writeBatch(db);
      for (const invoice of chunk) {
        const invoiceRef = doc(db, 'users', userId, 'invoices', invoice.id);
        invBatch.set(invoiceRef, {
          ...invoice,
          migratedAt: serverTimestamp(),
        });
      }
      await invBatch.commit();
    }
  }

  // Pension contributions
  if (localData.pensionPlan?.contributions && localData.pensionPlan.contributions.length > 0) {
    const contribBatches = chunkArray(localData.pensionPlan.contributions, 400);
    for (const chunk of contribBatches) {
      const contribBatch = writeBatch(db);
      for (const contribution of chunk) {
        const contribRef = doc(db, 'users', userId, 'pensionContributions', contribution.id);
        contribBatch.set(contribRef, {
          ...contribution,
          migratedAt: serverTimestamp(),
        });
      }
      await contribBatch.commit();
    }
  }

  return true;
}

// Helper per dividere array in chunk
function chunkArray(array, size) {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

// ========== APP SETTINGS ==========

export async function getAppSettings(userId) {
  const docRef = doc(db, 'users', userId, 'data', 'appSettings');
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : null;
}

export async function saveAppSettings(userId, settings) {
  const docRef = doc(db, 'users', userId, 'data', 'appSettings');
  await setDoc(docRef, {
    ...settings,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export function subscribeToAppSettings(userId, callback) {
  const docRef = doc(db, 'users', userId, 'data', 'appSettings');
  return onSnapshot(docRef, (docSnap) => {
    callback(docSnap.exists() ? docSnap.data() : null);
  });
}

// ========== CLIENTS ==========

export async function saveClient(userId, client) {
  const { id, ...data } = client;
  const docRef = id
    ? doc(db, 'users', userId, 'clients', id)
    : doc(collection(db, 'users', userId, 'clients'));

  await setDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });

  return docRef.id;
}

export async function updateClient(userId, clientId, updates) {
  const docRef = doc(db, 'users', userId, 'clients', clientId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteClient(userId, clientId) {
  const docRef = doc(db, 'users', userId, 'clients', clientId);
  await deleteDoc(docRef);
}

export function subscribeToClients(userId, callback) {
  const collRef = collection(db, 'users', userId, 'clients');
  const q = query(collRef, orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const clients = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(clients);
  });
}

// ========== TIMESHEETS ==========

export async function saveTimesheet(userId, timesheet) {
  const { id, ...data } = timesheet;
  const docRef = id
    ? doc(db, 'users', userId, 'timesheets', id)
    : doc(collection(db, 'users', userId, 'timesheets'));

  await setDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });

  return docRef.id;
}

export async function updateTimesheet(userId, timesheetId, updates) {
  const docRef = doc(db, 'users', userId, 'timesheets', timesheetId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteTimesheet(userId, timesheetId) {
  const docRef = doc(db, 'users', userId, 'timesheets', timesheetId);
  await deleteDoc(docRef);
}

export function subscribeToTimesheets(userId, callback) {
  const collRef = collection(db, 'users', userId, 'timesheets');
  const q = query(collRef, orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const timesheets = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(timesheets);
  });
}

// ========== F24 ==========

export async function saveF24(userId, f24) {
  const { id, ...data } = f24;
  const docRef = id
    ? doc(db, 'users', userId, 'f24', id)
    : doc(collection(db, 'users', userId, 'f24'));

  await setDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });

  return docRef.id;
}

export async function updateF24(userId, f24Id, updates) {
  const docRef = doc(db, 'users', userId, 'f24', f24Id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteF24(userId, f24Id) {
  const docRef = doc(db, 'users', userId, 'f24', f24Id);
  await deleteDoc(docRef);
}

export function subscribeToF24(userId, callback) {
  const collRef = collection(db, 'users', userId, 'f24');
  const q = query(collRef, orderBy('dataScadenza', 'asc'));
  return onSnapshot(q, (snapshot) => {
    const list = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(list);
  });
}

// ── Email Log ──

export async function logEmail(userId, emailData) {
  console.log('[logEmail] Salvataggio email log:', { userId, type: emailData.type, clientName: emailData.clientName });
  try {
    const collRef = collection(db, 'users', userId, 'emailLog');
    const docRef = await addDoc(collRef, {
      ...emailData,
      createdAt: serverTimestamp(),
    });
    console.log('[logEmail] Salvato con successo, id:', docRef.id);
    return docRef.id;
  } catch (err) {
    console.error('[logEmail] Errore salvataggio:', err);
    throw err;
  }
}

export async function getEmailLogByClient(userId, clientId) {
  const collRef = collection(db, 'users', userId, 'emailLog');
  const q = query(collRef, where('clientId', '==', clientId), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export function subscribeToEmailLog(userId, callback) {
  const collRef = collection(db, 'users', userId, 'emailLog');
  const q = query(collRef, orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const emails = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(emails);
  });
}

// ========== GDPR ==========

// Art. 20 — Scarica i miei dati
export async function exportUserData(userId) {
  const userData = {
    exportDate: new Date().toISOString(),
    profile: null,
    fiscalSettings: null,
    appSettings: null,
    pensionPlan: null,
    invoices: [],
    clients: [],
    timesheets: [],
    pensionContributions: [],
    emailLog: [],
    f24: [],
  };

  // Documenti singoli sotto users/{userId}/data/
  const profileSnap = await getDoc(doc(db, 'users', userId, 'data', 'profile'));
  if (profileSnap.exists()) userData.profile = profileSnap.data();

  const fiscalSnap = await getDoc(doc(db, 'users', userId, 'data', 'fiscalSettings'));
  if (fiscalSnap.exists()) userData.fiscalSettings = fiscalSnap.data();

  const appSettingsSnap = await getDoc(doc(db, 'users', userId, 'data', 'appSettings'));
  if (appSettingsSnap.exists()) userData.appSettings = appSettingsSnap.data();

  const pensionPlanSnap = await getDoc(doc(db, 'users', userId, 'data', 'pensionPlan'));
  if (pensionPlanSnap.exists()) userData.pensionPlan = pensionPlanSnap.data();

  // Subcollection
  const subcollections = [
    { name: 'invoices', key: 'invoices' },
    { name: 'clients', key: 'clients' },
    { name: 'timesheets', key: 'timesheets' },
    { name: 'pensionContributions', key: 'pensionContributions' },
    { name: 'emailLog', key: 'emailLog' },
    { name: 'f24', key: 'f24' },
  ];

  for (const sub of subcollections) {
    const snap = await getDocs(collection(db, 'users', userId, sub.name));
    snap.forEach((d) => userData[sub.key].push({ id: d.id, ...d.data() }));
  }

  return userData;
}

// Art. 17 — Elimina account (tutti i dati Firestore)
export async function deleteUserAccount(userId) {
  const subcollections = [
    'invoices',
    'clients',
    'timesheets',
    'pensionContributions',
    'emailLog',
    'f24',
  ];

  // Elimina tutti i documenti nelle subcollection
  for (const subName of subcollections) {
    const snap = await getDocs(collection(db, 'users', userId, subName));
    const batches = chunkArray(snap.docs, 400);
    for (const chunk of batches) {
      const b = writeBatch(db);
      chunk.forEach((d) => b.delete(d.ref));
      await b.commit();
    }
  }

  // Elimina documenti sotto users/{userId}/data/
  const dataIds = ['profile', 'fiscalSettings', 'appSettings', 'pensionPlan'];
  const dataBatch = writeBatch(db);
  for (const dataId of dataIds) {
    dataBatch.delete(doc(db, 'users', userId, 'data', dataId));
  }
  await dataBatch.commit();

  // Elimina rateLimits/{userId}/daily/*
  const rateLimitSnap = await getDocs(collection(db, 'rateLimits', userId, 'daily'));
  if (!rateLimitSnap.empty) {
    const rateBatch = writeBatch(db);
    rateLimitSnap.forEach((d) => rateBatch.delete(d.ref));
    await rateBatch.commit();
  }

  // Elimina documenti root
  await deleteDoc(doc(db, 'users', userId));
  await deleteDoc(doc(db, 'rateLimits', userId));

  return true;
}
